import { Component, EventEmitter, Input, Output, OnInit, Inject } from 'ng-metadata/core';
import * as angular from 'angular';

import { MediaContactBeat, MediaContactDocument } from './media-contact-document.model';
import { MediaDocument } from './../media-document/media-document.model';
import './media-contact-document.component.scss';

/**
 * @ngdoc directive
 * @name mwMediaContactDocument
 * @module mw.components.contentDocuments
 *
 * @param {object} [contact] - The content document. See
 * [Media Conctact Document Model](https://github.com/meltwater/gaf-ng-components/blob/master/src/lib/components/media-contact-document/media-contact-document.model.ts)
 *
 * @param {boolean} [is-selectable] - Control if the document is selectable (if true, shows a checkbox).
 * @param {boolean} [is-avatar-visible=true] - Control if the avatar is visible.
 * @param {boolean} [is-beats-visible=true] - Control if the beats are visible.
 * @param {string} [url] - Base URL to be used when Name or Source are clicked.
 * @param {boolean} [is-loading-relevant-articles] - Control if the loading animation is visible.
 * @param {boolean} [is-social-echo-available] - Controls the visibility of the social echo in the media document.
 * @param {expression} [on-select-toggle] - Angular expression to evaluate when the selected state of the document is toggled. $event is a boolean indicating selected(true)/unselected(false) state.
 *
 * @description
 * `<mw-media-contact-document>` is a component used to display a single media content document.
 *
 *
 * @usage
 * <hljs hljs-language="html">
 *           <mw-media-contact-document
 *               ng-repeat="contact in $ctrl.contactDocuments"
 *              contact="contact"
 *              is-selectable="$ctrl.displaySettings.isSelectable"
 *              is-contact-selected="$ctrl.isContactSelected"
 *              is-avatar-visible="$ctrl.displaySettings.isAvatarVisible"
 *              is-article-keywords-visible="$ctrl.displaySettings.isArticleKeywordsVisible"
 *              on-select-toggle="$ctrl.onSelectToggle(contact)"
 *              url="$ctrl.icmWebUrl"
 *              loading-contact-id="contact.id">
 *              <mw-content-document-footer>
 *                  <mw-content-document-action-trigger
 *                      ng-if="$ctrl.detailsAvailable(contact)"
 *                      on-click="contact.activeTray = contact.activeTray !== 'details' ? 'details' : ''"
 *                      is-active="contact.activeTray === 'details'"
 *                      text="::('mw-media-contact-document__contact-details' | translate)">
 *                  </mw-content-document-action-trigger>
 *                  <mw-content-document-action-trigger
 *                      on-click="::$ctrl.handleRelevantArticlesToggle(contact)"
 *                      text="::'mw-media-contact-document__contact-details--relevant-articles' | translate:{count: contact.relevantArticlesCount}:'messageformat'">
 *                  </mw-content-document-action-trigger>
 *              </mw-content-document-footer>
 *          </mw-media-contact-document>
 * </hljs>
 */

const mediaContactDocumentTemplate = `
    <div class="mw-media-contact-document" ng-class="{'mw-media-contact-document--is-selectable': $ctrl.isSelectable, 'mw-content-document--avatar-is-visible': $ctrl.isAvatarVisible}">
        <div class="mw-content-document__main-container" layout="row">
            <div class="mw-media-contact-document__checkbox-container" ng-if="::$ctrl.isSelectable">
                <md-checkbox
                    aria-label="Select Contact"
                    ng-checked="$ctrl.contact.selected"
                    ng-click="::$ctrl.handleSelectToggle($ctrl.contact)"
                    class="md-primary">
                </md-checkbox>
            </div>
            <div class="mw-media-contact-document__column-two" flex="100">
                <mw-media-contact-document-header>
                    <mw-content-document-source-avatar
                        ng-if="::$ctrl.isAvatarVisible"
                        source-name="::$ctrl.contact.fullName"
                        initials-size="2">
                    </mw-content-document-source-avatar>
                    <mw-content-document-author
                        class="mw-media-contact-document__author"
                        url="::$ctrl.url"
                        id="::$ctrl.contact.id"
                        text="::$ctrl.contact.fullName">
                    </mw-content-document-author>
                    <mw-media-contact-document-title
                        text="::$ctrl.contact.jobTitle">
                    </mw-media-contact-document-title>
                    <mw-media-contact-document-source
                        url="::$ctrl.url"
                        source="::$ctrl.contact.primarySource">
                    </mw-media-contact-document-source>
                    <mw-media-contact-document-address
                        address="::$ctrl.contact.primaryAddress">
                    </mw-media-contact-document-address>
                    <mw-content-document-actions>
                        <ng-transclude ng-transclude-slot="mwContentDocumentActions"></ng-transclude>
                    </mw-content-document-actions>
                </mw-media-contact-document-header>
                <div class="mw-content-document__content">
                    <ng-transclude ng-transclude-slot="mwContentDocumentKeywords"></ng-transclude>
                </div>
                <ng-transclude class="mw-content-document__footer-slot" ng-transclude-slot="mwContentDocumentFooter">
                </ng-transclude>
            </div>
        </div>
        <mw-content-document-tray>
            <ng-transclude ng-transclude-slot="mwContentDocumentTrayContent">
            </ng-transclude>
        </mw-content-document-tray>
    </div>
`;

@Component({
    selector: 'mw-media-contact-document',
    template: mediaContactDocumentTemplate,
    legacy: {
        transclude: {
            'mwContentDocumentActions': '?mwContentDocumentActions',
            'mwContentDocumentKeywords': '?mwContentDocumentKeywords',
            'mwContentDocumentFooter': '?mwContentDocumentFooter',
            'mwContentDocumentTrayContent': '?mwContentDocumentTrayContent'
        }
    }
})
export class MediaContactDocumentComponent implements OnInit {
    @Input('<') isSelectable: boolean;
    @Input('<') contact: MediaContactDocument;
    @Input('<') isAvatarVisible: boolean;
    @Input('<') url: string;

    private show = this.$translate.instant('Show');

    @Output() onSelectToggle = new EventEmitter<{ contact: MediaContactDocument }>();

    constructor(
        @Inject('$transclude') private $transclude: ng.ITranscludeFunction,
        @Inject('$scope') private $scope,
        @Inject('$timeout') private $timeout,
        @Inject('$window') private $window,
        @Inject('$mdUtil') private $mdUtil,
        @Inject('$translate') private $translate: ng.translate.ITranslateService
    ) {
        'ngInject';
    }

    ngOnInit() {
        this.isAvatarVisible = this.isAvatarVisible === false ? false : true;
    }

    handleSelectToggle(contact: MediaContactDocument) {
        this.onSelectToggle.emit({contact});
    }

}



// WEBPACK FOOTER //
// ./src/lib/components/content-documents/media-contact-document/media-contact-document.component.ts